<script>
import BaseContainer from '@/components/default/views/shared/BaseContainer'
import BrandHeader from '@/components/default/views/shared/BrandHeader'
import BrowserAlert from '@/components/default/views/landing/BrowserAlert'
import branding from '@/services/branding'
import { isAdmin } from '@/helpers/auth'
import { mapGetters } from 'vuex'
import Noty from 'noty'
import Vue from 'vue'
import VueGtm from 'vue-gtm'

const isProd = process.env.NODE_ENV === 'production'

export default {
	name: 'login',
	data: () => ({
		showBranchLogin: true,
		employeeName: null,
		branchCode: null,
		username: null,
		password: null,
		supportedBrowser: true
	}),
	components: {
		BaseContainer,
		BrandHeader,
		BrowserAlert
	},
	computed: {
		...mapGetters(['client'])
	},
	mounted: async function () {
		const tracker = await branding.getTracker()
		const isBrandProd = (window.location.host.indexOf(tracker.serverName) >= 0)
		Vue.use(VueGtm, {
			id: [tracker.gtmId],
			debug: !isProd,
			enabled: isBrandProd,
			loadScript: true
		})
		this.trackGA()
		this._showErrorMessage(this.$t('commonMsg.youNeedLogin'))
	},
	methods: {
		submit: function (e) {
			e.preventDefault()
			let loginData
			if (!this.showBranchLogin) {
				loginData = { username: this.username, password: this.password }
			} else {
				loginData = { name: this.employeeName, branchCode: this.branchCode }
			}
			this.$store.dispatch('loadUser', loginData).then((user) => {
				const target = isAdmin(user) ? 'dashboard' : 'enrollment'
				this.$router.push({
					name: target
				})
			}).catch(() => {
				if (this.showBranchLogin) {
					this._showErrorMessage(this.$t('commonMsg.incorrectBranchCode'))
				} else {
					this._showErrorMessage(this.$t('commonMsg.incorrectLoginPassword'))
				}
			})
		},
		_showErrorMessage: function (m) {
			new Noty({
				text: m,
				layout: 'bottomCenter',
				timeout: 2500
			}).show()
		},
		setLang: function (lang) {
			this.$i18n.locale = lang
		},
		handleInvalid: function (m) {
			new Noty({
				text: m,
				type: 'warning',
				layout: 'bottomCenter',
				timeout: 2500
			}).show()
		},
		trackGA: function () {
			if (this.$ga) {
				this.$ga.page({
					page: this.$route.meta.analytics.page,
					title: this.$route.meta.analytics.title,
					location: window.location.href
				})
			}
			if (this.$gtm.enabled()) {
				this.$gtm.trackView(
					this.$route.meta.gtm,
					'/' + this.$route.name)
			}
		}
	}
}
</script>

<template>
	<div>
		<brand-header :show-user="false" />
		<browser-alert @compatible-device="supportedBrowser = $event" />
		<base-container class="login" modifiers="default-width height padding column-layout" :class="{'login__alert' : !supportedBrowser}">
			<h1 class="login__title">{{ $t("login.userAuth") }}</h1>
			<form @submit="submit" autocomplete="off" v-show="!showBranchLogin">
				<div class="login__group">
					<label for="username" class="login__label">{{ $t("login.username") }}</label>
					<input v-model="username" id="username" name="username" type="text" class="login__input" title=""
						@invalid.capture.prevent="handleInvalid($t('commonMsg.username'))" required/>
				</div>
				<div class="login__group">
					<label for="password" class="login__label">{{ $t("login.password") }}</label>
					<input v-model="password" id="password" name="password" type="password" class="login__input" title=""
						@invalid.capture.prevent="handleInvalid($t('commonMsg.password'))" required/>
				</div>
				<base-button type="submit" class="button login__submit" modifiers="primary">{{ $t("login.submit") }}</base-button>
			</form>
			<form @submit="submit" autocomplete="off" v-show="showBranchLogin">
				<div class="login__group">
					<label for="employee-name" class="login__label">{{ $t("login.employeeName") }}</label>
					<input v-model="employeeName" id="employee-name" name="employee-name" type="text" class="login__input" title=""
						@invalid.capture.prevent="handleInvalid($t('commonMsg.nameEmployed'))" required/>
				</div>
				<div class="login__group">
					<label for="branch-code" class="login__label">{{ $t("login.branchCode") }}</label>
					<input v-model="branchCode" id="branch-code" name="branch-code" type="text" class="login__input" title=""
						@invalid.capture.prevent="handleInvalid($t('commonMsg.branchCode'))" required/>
				</div>
				<base-button type="submit" class="button login__submit" modifiers="primary">{{ $t("login.submit") }}</base-button>
			</form>
			<div class="login__language">
				<span v-show="showBranchLogin">{{ $t("login.agentLogin") }}</span>
				<a v-show="!showBranchLogin" v-on:click="showBranchLogin = true" href="#" class="login__language--set">{{ $t("login.agentLogin") }}</a>
				|
				<span v-show="!showBranchLogin">{{ $t("login.adminLogin") }}</span>
				<a v-show="showBranchLogin" v-on:click="showBranchLogin = false" href="#" class="login__language--set">{{ $t("login.adminLogin") }}</a>
			</div>
			<div class="login__language">
				<span v-show="$i18n.locale == 'ja'">日本語</span>
				<a v-show="$i18n.locale != 'ja'" v-on:click="setLang('ja')" href="#" class="login__language--set">日本人</a>
				|
				<span v-show="$i18n.locale == 'en'">English</span>
				<a v-show="$i18n.locale != 'en'" v-on:click="setLang('en')" href="#" class="login__language--set">English</a>
			</div>
		</base-container>
	</div>
</template>

<style lang="scss">
	.login {
		color: #575757;
		padding-top: 15vh;
		&__alert {
			padding-top: 0;
		}
		&__title {
			font-size: 1.5rem;
			font-weight: 600;
			padding-top: 1rem;
			padding-bottom: 1rem;
		}
		&__item {
			margin: 1rem 0 0 0;
			@media (max-width: 420px) {
				font-size: 3.2vw;
			}
		}
		&__icon {
			font-size: 3rem;
			color: #333333;
		}
		&__modal-title {
			font-weight: bold;
			font-size: 1.5rem;
			@media (max-width: 420px) {
				font-size: 4vw;
			}
		}
		&__group {
			display: flex;
			flex-direction: column;
			margin: 1rem 0 1rem 0;
		}
		&__label {
			margin-bottom: 0.5rem;
			font-weight: bold;
		}
		&__input {
			font-size: 1.1rem;
			text-overflow: ellipsis;
			padding: 0.5rem;
			border: solid 1px #6b6a8b;
			border-radius: 4px;
			height: 2.5rem;
			&:disabled {
				background-color:#EEE;
				color:rgb(87,87,87);
			}
			&:invalid {
					background-color: #fff5db;
			}
		}
		&__submit {
			width: 100%;
			margin-top: 1rem;
			margin-bottom: 1rem;
		}
		&__language {
			margin-top: 3rem;
			text-align: center;
			&--set {
				text-decoration: underline;
			}
		}
	}
</style>
